import { Copy, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ReceiptDetailRow } from '@/components/receipts/ReceiptDetailRow';
import {
  copyText,
  formatExamPinCredential,
  formatReceiptCurrency,
} from '@/utils/receiptFormatters';
import type { BillReceiptData, ExamPinCredential } from '@/types/receipt';
import { notify } from '@/utils/notify';

interface ExamReceiptProps {
  receipt: BillReceiptData;
  compact?: boolean;
}

function ExamPinCard({ entry, index }: { entry: ExamPinCredential; index: number }) {
  const label = formatExamPinCredential(entry);
  const copyValue = entry.pin || label;

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
      <div className="mb-1.5 flex items-center justify-center gap-1.5">
        <GraduationCap className="h-3.5 w-3.5 shrink-0 text-amber-700" />
        <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-800 sm:text-xs">
          Exam PIN {index > 0 ? index + 1 : ''}
        </span>
      </div>
      {entry.serial && (
        <p className="text-center text-xs text-amber-900">
          Serial: <span className="font-mono font-semibold">{entry.serial}</span>
        </p>
      )}
      {entry.pin && (
        <p className="mt-1 break-all text-center font-mono text-sm font-bold leading-snug tracking-wide text-amber-950 sm:text-base">
          {entry.pin}
        </p>
      )}
      {!entry.pin && !entry.serial && (
        <p className="break-all text-center font-mono text-sm font-bold text-amber-950">{label}</p>
      )}
      <Button
        variant="outline"
        size="sm"
        fullWidth
        className="mt-2"
        onClick={() =>
          void copyText(copyValue).then((ok) =>
            ok ? notify.success('PIN copied') : notify.error('Could not copy PIN'),
          )
        }
      >
        <Copy className="h-4 w-4" />
        Copy PIN
      </Button>
    </div>
  );
}

export function ExamReceipt({ receipt, compact = false }: ExamReceiptProps) {
  const pins =
    receipt.examPins && receipt.examPins.length > 0
      ? receipt.examPins
      : receipt.purchasedCode
        ? [{ serial: '', pin: receipt.purchasedCode }]
        : [];

  return (
    <div className="mx-auto w-full min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-soft">
      <div className="bg-gradient-to-r from-secondary-500 to-primary px-4 py-3 text-white">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/20">
              <img src="/assets/logo-dark.jpeg" alt="AS DataLink" className="h-full w-full object-contain p-1" />
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-sm font-bold sm:text-base">AS DataLink</h2>
              <p className="text-[11px] opacity-90 sm:text-xs">Exam PIN Purchase</p>
            </div>
          </div>
          <div className="shrink-0 text-right text-base font-bold sm:text-lg">
            {formatReceiptCurrency(receipt.amountPaid)}
          </div>
        </div>
      </div>

      <div className={`space-y-3 ${compact ? 'p-3' : 'p-4'}`}>
        <div className="flex justify-center">
          <div className="flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-[11px] font-medium text-green-800 sm:text-xs">
            <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
            PAYMENT SUCCESSFUL
          </div>
        </div>

        {pins.map((entry, index) => (
          <ExamPinCard key={`${entry.serial}-${entry.pin}-${index}`} entry={entry} index={index} />
        ))}

        <div>
          <ReceiptDetailRow label="Provider" value={receipt.providerName} />
          <ReceiptDetailRow label="Plan" value={receipt.planName ?? '—'} />
          <ReceiptDetailRow label="Candidate" value={receipt.candidateName ?? '—'} />
          <ReceiptDetailRow label="Phone" value={receipt.phone ?? '—'} mono />
          {receipt.profileId && (
            <ReceiptDetailRow label="Profile ID" value={receipt.profileId} mono />
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-0.5">
          <ReceiptDetailRow label="Transaction No." value={receipt.reference} mono />
          <ReceiptDetailRow label="Date & Time" value={`${receipt.date} at ${receipt.time}`} />
        </div>

        {!compact && (
          <div className="border-t border-gray-200 pt-3 text-center text-xs text-gray-500">
            <p className="font-medium">Thank you for using AS DataLink</p>
            <p className="mt-1">Keep your PIN safe for registration</p>
          </div>
        )}
      </div>
    </div>
  );
}
